import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../utils/api';
import config from '../../config';
import { FileItem } from '../../types/files';

import { getDriveApiToken } from '../../lib/auth-token';
export interface FileState {
  files: FileItem[];
  recentFiles: FileItem[];
  favoriteFiles: FileItem[];
  currentFile: FileItem | null;
  currentFolder: string | null;
  folderPath: { id: string; name: string }[];
  loading: boolean;
  error: string | null;
  selectedFiles: string[];
}

const initialState: FileState = {
  files: [],
  recentFiles: [],
  favoriteFiles: [],
  currentFile: null,
  currentFolder: null,
  folderPath: [],
  loading: false,
  error: null,
  selectedFiles: []
};

// Helper to get axios config with auth token
const getAxiosConfig = () => {
  const token = getDriveApiToken();
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Async Thunks
export const fetchFiles = createAsyncThunk(
  'files/fetchFiles',
  async (folderId: string | null = null, { rejectWithValue }) => {
    try {
      const url = folderId 
        ? `/files?folderId=${folderId}` 
        : '/files';
      
      const response = await api.get(url, getAxiosConfig());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch files');
    }
  }
);

const normalizeFileArray = (data: unknown): FileItem[] => {
  if (Array.isArray(data)) return data as FileItem[];
  if (data && typeof data === 'object') {
    const obj = data as { files?: unknown; items?: unknown };
    if (Array.isArray(obj.files)) return obj.files as FileItem[];
    if (Array.isArray(obj.items)) return obj.items as FileItem[];
  }
  return [];
};

export const fetchRecentFiles = createAsyncThunk(
  'files/fetchRecentFiles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/files/recent', getAxiosConfig());
      // Always return an array, regardless of backend response shape
      if (Array.isArray(response.data)) return response.data;
      if (Array.isArray(response.data.files)) return response.data.files;
      if (Array.isArray(response.data.items)) return response.data.items;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch recent files');
    }
  }
);

export const uploadFile = createAsyncThunk(
  'files/uploadFile',
  async ({ file, folderId }: { file: File, folderId?: string }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (folderId) {
        formData.append('folderId', folderId);
      }
      
      const response = await api.post('/files/upload', formData, {
        timeout: config.upload.requestTimeoutMs,
        headers: {
          'Authorization': `Bearer ${getDriveApiToken()}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          // Progress tracking logic can be added here
        }
      });
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload file');
    }
  }
);

export const starFile = createAsyncThunk(
  'files/starFile',
  async ({ fileId, isStarred }: { fileId: string, isStarred: boolean }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/files/${fileId}/star`,
        { isStarred },
        getAxiosConfig()
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update star status');
    }
  }
);

export const downloadFile = createAsyncThunk(
  'files/downloadFile',
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/files/${fileId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to download file');
    }
  }
);

export const deleteFile = createAsyncThunk(
  'files/deleteFile',
  async (fileId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/files/${fileId}`);
      return fileId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete file');
    }
  }
);

export const toggleFileStarred = createAsyncThunk(
  'files/toggleFileStarred',
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/files/${fileId}/star`);
      return { id: fileId, isStarred: response.data.isStarred };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle file star status');
    }
  }
);

export const fetchFavoriteFiles = createAsyncThunk(
  'files/fetchFavoriteFiles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/files/favorites', getAxiosConfig());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch favorite files');
    }
  }
);

export const fetchFileById = createAsyncThunk(
  'files/fetchFileById',
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/files/${fileId}`);
      // Return the file object. If API nests it under `file`, use that, otherwise use root data.
      const data = response.data;
      return (data && (data.file ?? data)) as any;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch file details');
    }
  }
);

const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    setCurrentFolder: (state, action: PayloadAction<string | null>) => {
      state.currentFolder = action.payload;
    },
    setFolderPath: (state, action: PayloadAction<{ id: string; name: string }[]>) => {
      state.folderPath = action.payload;
    },
    setSelectedFiles: (state, action: PayloadAction<string[]>) => {
      state.selectedFiles = action.payload;
    },
    addSelectedFile: (state, action: PayloadAction<string>) => {
      if (!state.selectedFiles.includes(action.payload)) {
        state.selectedFiles.push(action.payload);
      }
    },
    removeSelectedFile: (state, action: PayloadAction<string>) => {
      state.selectedFiles = state.selectedFiles.filter(id => id !== action.payload);
    },
    clearSelectedFiles: (state) => {
      state.selectedFiles = [];
    },
    clearFilesError: (state) => {
      state.error = null;
    },
    setCurrentFile: (state, action: PayloadAction<FileItem | null>) => {
      state.currentFile = action.payload;
    },
    clearCurrentFile: (state) => {
      state.currentFile = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Files
      .addCase(fetchFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFiles.fulfilled, (state, action: PayloadAction<FileItem[]>) => {
        state.loading = false;
        state.files = normalizeFileArray(action.payload);
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Recent Files
      .addCase(fetchRecentFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentFiles.fulfilled, (state, action: PayloadAction<FileItem[]>) => {
        state.loading = false;
        const normalized = normalizeFileArray(action.payload);
        state.files = normalized;
        state.recentFiles = normalized;
      })
      .addCase(fetchRecentFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Favorite Files
      .addCase(fetchFavoriteFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFavoriteFiles.fulfilled, (state, action: PayloadAction<FileItem[]>) => {
        state.loading = false;
        state.favoriteFiles = normalizeFileArray(action.payload);
      })
      .addCase(fetchFavoriteFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch File by ID
      .addCase(fetchFileById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFileById.fulfilled, (state, action: PayloadAction<FileItem>) => {
        state.loading = false;
        state.currentFile = action.payload;
      })
      .addCase(fetchFileById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Upload File
      .addCase(uploadFile.fulfilled, (state, action: PayloadAction<FileItem>) => {
        state.files.push(action.payload);
      })
      
      // Star File
      .addCase(starFile.fulfilled, (state, action: PayloadAction<FileItem>) => {
        const index = state.files.findIndex(file => file.id === action.payload.id);
        if (index !== -1) {
          state.files[index].isStarred = action.payload.isStarred;
        }
      })
      
      // Download File
      .addCase(downloadFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete File
      .addCase(deleteFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.loading = false;
        if (!Array.isArray(state.files)) {
          state.files = [];
        }
        state.files = state.files.filter(file => file.id !== action.payload);
        if (state.currentFile?.id === action.payload) {
          state.currentFile = null;
        }
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Toggle File Starred
      .addCase(toggleFileStarred.fulfilled, (state, action) => {
        const { id, isStarred } = action.payload;
        const fileIndex = state.files.findIndex(file => file.id === id);
        if (fileIndex !== -1) {
          state.files[fileIndex].isStarred = isStarred;
        }
        if (state.currentFile?.id === id) {
          state.currentFile.isStarred = isStarred;
        }
      })
      .addCase(toggleFileStarred.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const {
  setCurrentFolder,
  setFolderPath,
  setSelectedFiles,
  addSelectedFile,
  removeSelectedFile,
  clearSelectedFiles,
  clearFilesError,
  setCurrentFile,
  clearCurrentFile
} = filesSlice.actions;

export default filesSlice.reducer; 